import { cloneElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useMediaQuery } from "react-responsive";

import Modal from "../Modal";
import { Button } from "../Button";
import PostHeader from "../PostHeader";
import PostFooter from "../PostFooter";
import PostComments from "../PostComments";
import DeletedContentModal from "../DeletedContentModal";
import ViewPostModalContent from "../ViewPostModalContent";
import MediaPostModalFooter from "@components/MediaPostModalFooter";
import {
  getPostDetails,
  getPostDetailsReset,
} from "@redux/postDetails/actions";
import { followCreatorReset } from "@redux/creatorProfile/actions";
import { localstorageKeys } from "@const/store";
import { selectPostById } from "@redux/posts/selectors";
import routes from "../../const/routes";
import { XL } from "@const/breakpoints";
import { useService } from "@/base/hooks/useService";
import { StorageService } from "@services/StorageService";
import { ReactComponent as IconArrow } from "../../assets/ic-arrow-right.svg";

import styles from "./index.module.scss";

const ViewPostModal = ({ withArrows, arrowsDisabled, postKey }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const storageService = useService(StorageService);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const searchParams = new URLSearchParams(search);
  const { username } = useSelector(({ profileReducer }) => profileReducer);

  const { accessToken } = storageService.get(localstorageKeys.session, {});

  const {
    id,
    error,
    title,
    content,
    isLiked,
    isLocked,
    description,
    publishedDate,
    author: authorId,
    likesCount = 21,
    commentsCount = 1,
    accessType,
    isLikeActionAvailable,
    isCommentActionAvailable,
    isBlurredByReports,
    showBlurredPostAnywhere,
    subscriptions,
    priceType,
    previewImage,
    socialContent,
    socialType,
    nextPostId,
    nextPostKey,
    prevPostId,
    prevPostKey,
    isLiveStreamChatDisplayed,
    stream,
  } = useSelector(({ postDetailsReducer }) => postDetailsReducer);

  const { showBlurredPostAnywhere: posts } = useSelector((state) =>
    selectPostById(state, id),
  );

  const { isFollowSuccess } = useSelector(
    ({ followersReducer }) => followersReducer,
  );

  const isBlurredPost =
    isBlurredByReports && !showBlurredPostAnywhere && !posts;

  const author = useSelector(
    ({ authorsReducer }) => authorsReducer?.[authorId] || {},
  );

  const myUsername = useSelector(
    ({ profileReducer }) => profileReducer.username,
  );

  const hasNext = (nextPostId && nextPostKey) || arrowsDisabled;
  const hasPrev = (prevPostId && prevPostKey) || arrowsDisabled;

  const handleArrowClick = (isNext = false) => {
    const nextOffset = searchParams.get("o");
    searchParams.set("o", `${isNext ? +nextOffset + 1 : +nextOffset - 1}`);
    searchParams.set("postId", isNext ? nextPostId : prevPostId);
    searchParams.set("postBySub", isNext ? nextPostKey : prevPostKey);

    dispatch(getPostDetails({ uniqueKey: searchParams.get("postBySub") }));

    navigate(`${pathname}?${searchParams.toString()}`, {
      pathname,
      replace: true,
      search: `?${searchParams.toString()}`,
    });
  };

  useEffect(() => {
    dispatch(getPostDetails({ uniqueKey: postKey }));
  }, []);

  useEffect(() => {
    if (!isFollowSuccess) return;
    dispatch(getPostDetails({ uniqueKey: postKey }));
    dispatch(followCreatorReset());
  }, [isFollowSuccess, postKey]);

  useEffect(() => {
    if (error) {
      setIsOpenModal(true);
    }
  }, [error, isOpenModal]);

  const toggleErrorModal = () => {
    setIsOpenModal((isOpen) => !isOpen);
    accessToken
      ? navigate(`${routes.CREATOR_PROFILE()}${username}`, { replace: true })
      : navigate(location.pathname, { replace: true });
  };

  const isDesktop = useMediaQuery({
    query: `(min-width: ${XL})`,
  });

  if (error) {
    return (
      <DeletedContentModal
        isOpen={isOpenModal}
        title="DELETED_POST_TITLE"
        bodyText="POST_NO_LONGER_AVAILABLE"
        onCancel={toggleErrorModal}
        onClose={toggleErrorModal}
      />
    );
  }
  const toggleModal = () => {
    searchParams.delete("o");
    searchParams.delete("postId");
    searchParams.delete("postBySub");
    searchParams.delete("subId");
    searchParams.delete("view");

    const search = searchParams.toString();

    navigate(`${location.pathname}?${search}`, {
      pathname: location.pathname,
      hash: location.hash,
      search,
      state: { refreshId: Date.now() },
    });
    dispatch(getPostDetailsReset());
  };

  const postHeader = (
    <PostHeader
      postId={id}
      uniqueKey={postKey}
      toggleModal={toggleModal}
      wrapperClassName={classNames("flex-between", styles.author)}
      avatarClassName={styles.avatar}
      avatarContainerClassName={styles.avatarContainer}
      publishedDate={publishedDate}
      author={author}
      isLocked={isLocked}
      accessToken={accessToken}
      isPinnedOptionVisible={false}
    />
  );

  const postFooter = (
    <PostFooter
      modalView
      postId={id}
      commentButtonDisable
      uniqueKey={postKey}
      creatorId={author?.id}
      isLiked={isLiked}
      likesCount={likesCount}
      commentsCount={commentsCount}
      counterClassName="d-inline d-xl-none"
      counterSectionClassName="d-none d-xl-flex"
      className={classNames(styles.likesSection, "d-none d-xl-flex")}
      username={author?.username}
      isLikeActionAvailable={isLikeActionAvailable}
      isCommentActionAvailable={isCommentActionAvailable}
    />
  );

  const postFooterMobile = cloneElement(postFooter, {
    className: classNames(
      styles.likesSection,
      styles.likesSectionMobile,
      "d-xl-none bg-primary",
      isCommentActionAvailable && styles.bottomShift,
    ),
  });

  const arrowsMobile = (
    <div
      className={classNames(
        styles.arrowContainerMobile,
        "d-xl-none d-flex align-items-center",
        !withArrows && "d-none",
        isCommentActionAvailable && styles.arrowsBottomShift,
        (likesCount || commentsCount) && styles.arrowsBottomShiftFull,
      )}
    >
      {hasPrev && (
        <Button
          color="secondary"
          onClick={() => handleArrowClick(false)}
          className={classNames(
            "position-absolute align-items-center d-flex btn-resetStyle",
            styles.arrow,
            styles.arrowMobile,
            styles.prevMobile,
          )}
        >
          <IconArrow width={20} height={20} className="rotate-180" />
          <span className={classNames("text-body-2 text-regular", styles.text)}>
            Prev
          </span>
        </Button>
      )}
      {hasNext && (
        <Button
          color="secondary"
          onClick={() => handleArrowClick(true)}
          className={classNames(
            "position-absolute align-items-center d-flex btn-resetStyle",
            styles.arrow,
            styles.arrowMobile,
          )}
        >
          <span className={classNames("text-body-2 text-regular", styles.text)}>
            Next
          </span>
          <IconArrow width={20} height={20} />
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      centered={isDesktop}
      backdrop
      size="xl"
      closeClassName={classNames("m-0", styles.close)}
      isOpen={!!postKey}
      toggle={toggleModal}
      onClose={toggleModal}
      showCancel={false}
      showConfirm={false}
      className="modal-post"
      footerClassName="d-none"
      bodyClassName={classNames(
        "d-flex flex-column d-xl-grid p-0",
        styles.body,
      )}
      contentClassName={classNames("h-xl-auto", styles.content)}
      closeContainerClassName={styles.closeContainer}
      cancelButtonClassName={styles.closeBtn}
      dialogClassName={styles.dialog}
      isPostModal
    >
      {hasPrev && (
        <Button
          color="secondary"
          onClick={() => handleArrowClick(false)}
          className={classNames(
            "position-absolute align-items-center translate-y-middle top-50 d-none d-xl-flex flex-column btn-resetStyle",
            styles.arrow,
            styles.prev,
          )}
        >
          <IconArrow width={24} height={24} className="rotate-180 mb-1" />
          <span className={classNames("text-body-1 text-regular", styles.text)}>
            Prev
          </span>
        </Button>
      )}
      {hasNext && (
        <Button
          color="secondary"
          onClick={() => handleArrowClick(true)}
          className={classNames(
            "position-absolute align-items-center translate-y-middle top-50 d-none d-xl-flex flex-column btn-resetStyle",
            styles.arrow,
          )}
        >
          <IconArrow className="mb-1" width={24} height={24} />
          <span className={classNames("text-body-1 text-regular", styles.text)}>
            Next
          </span>
        </Button>
      )}
      <div className="d-xl-none">{postHeader}</div>
      <div
        className={classNames(
          "d-flex flex-column w-100",
          isLocked && "mb-auto",
          styles.media,
        )}
      >
        <ViewPostModalContent
          content={content}
          title={title}
          isLocked={isLocked}
          authorId={authorId}
          accessType={accessType}
          description={description}
          titleClassName={styles.title}
          username={author?.username}
          id={id}
          isBlurredByReports={isBlurredPost}
          subscriptions={subscriptions}
          priceType={priceType}
          previewImage={previewImage}
          socialContent={socialContent}
          socialType={socialType}
          uniqueKey={postKey}
          accessToken={accessToken}
          withArrows={withArrows}
          isDesktop={isDesktop}
          isLiveStreamChatDisplayed={isLiveStreamChatDisplayed}
        />
      </div>
      <div
        className={classNames(
          "position-relative d-none d-xl-flex flex-column bg-primary rounded",
          styles.sidebar,
        )}
      >
        <div className="d-none d-xl-block order-xl-0">{postHeader}</div>
        {postFooter}
        <hr
          className={classNames(
            "inputs-color opacity-100 order-xl-0",
            styles.divider,
            isLocked && "d-none d-xl-block",
          )}
        />

        {!isLocked && isDesktop && (
          <PostComments
            id={id}
            postKey={postKey}
            myUsername={myUsername}
            className="d-none d-xl-flex"
            allCommentsCount={commentsCount}
            isMyPost={author?.username === myUsername}
            isCommentActionAvailable={isCommentActionAvailable}
            isLiveStreamChatDisplayed={isLiveStreamChatDisplayed}
            streamUniqueKey={stream?.uniqueKey}
          />
        )}
      </div>

      {!isLocked && !isDesktop ? (
        <>
          <MediaPostModalFooter title={title} description={description} />
          <PostComments
            id={id}
            postKey={postKey}
            myUsername={myUsername}
            className="d-flex d-xl-none z-2"
            allCommentsCount={commentsCount}
            isMyPost={author?.username === myUsername}
            isCommentActionAvailable={isCommentActionAvailable}
            isLiveStreamChatDisplayed={isLiveStreamChatDisplayed}
            streamUniqueKey={stream?.uniqueKey}
            postFooterMobile={postFooterMobile}
          >
            <div className="position-sticky start-0 w-100 h-fit-content">
              {arrowsMobile}
            </div>
          </PostComments>
        </>
      ) : (
        <>
          {!isDesktop && (
            <MediaPostModalFooter title={title} description={description} />
          )}
          {arrowsMobile}
          {postFooterMobile}
        </>
      )}
    </Modal>
  );
};

export default ViewPostModal;
